import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { TimeRangeFields } from './TimeRangeFields';
import { updateBooking } from '../services/bookings';
import {
  BRANCA_LABELS,
  BRANCHE,
  Booking,
  BookingInput,
  Branca,
  isBranca,
  isValidEmail,
  isValidPhone,
  normalizePhone,
  sanitizePhoneInput,
} from '../types/booking';
import { isValidRange } from '../utils/slots';
import { colors } from '../theme';

interface EditBookingDialogProps {
  booking: Booking | null;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}

interface FormState {
  gruppoScout: string;
  branca: Branca | '';
  numeroRagazzi: string;
  numeroAdulti: string;
  referenteNome: string;
  telefono: string;
  email: string;
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  altreInfo: string;
}

function toForm(booking: Booking): FormState {
  return {
    gruppoScout: booking.gruppoScout,
    branca: booking.branca,
    numeroRagazzi: String(booking.numeroRagazzi),
    numeroAdulti: String(booking.numeroAdulti ?? 0),
    referenteNome: booking.referenteNome,
    telefono: booking.telefono,
    email: booking.email ?? '',
    startDate: booking.startDate,
    endDate: booking.endDate,
    startTime: booking.startTime,
    endTime: booking.endTime,
    altreInfo: booking.altreInfo,
  };
}

export function EditBookingDialog({
  booking,
  open,
  onClose,
  onSaved,
}: EditBookingDialogProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [form, setForm] = useState<FormState | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && booking) {
      setForm(toForm(booking));
      setError(null);
      setSaving(false);
    }
  }, [open, booking]);

  if (!booking || !form) {
    return null;
  }

  const sameDay = form.startDate === form.endDate;

  const update =
    (field: keyof FormState) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const raw = e.target.value;
      const value = field === 'telefono' ? sanitizePhoneInput(raw) : raw;
      setForm((prev) => (prev ? { ...prev, [field]: value } : prev));
      setError(null);
    };

  const handleSave = async () => {
    setError(null);
    const numero = Number(form.numeroRagazzi);
    const adulti = Number(form.numeroAdulti);

    if (!form.gruppoScout.trim()) {
      setError('Inserisci il gruppo scout.');
      return;
    }
    if (!isBranca(form.branca)) {
      setError('Seleziona per che branca prenoti.');
      return;
    }
    if (form.numeroRagazzi.trim() === '' || !Number.isFinite(numero) || numero < 0) {
      setError('Indica il numero di ragazzi previsti (anche 0).');
      return;
    }
    if (form.numeroAdulti.trim() === '' || !Number.isFinite(adulti) || adulti < 1) {
      setError('Indica un numero di adulti valido (almeno 1).');
      return;
    }
    if (!form.referenteNome.trim()) {
      setError('Inserisci nome e cognome del referente.');
      return;
    }
    if (!isValidPhone(form.telefono)) {
      setError('Inserisci un numero di cellulare valido.');
      return;
    }
    if (!isValidEmail(form.email.trim())) {
      setError('Inserisci un’email valida.');
      return;
    }
    if (!form.startDate || !form.endDate) {
      setError('Seleziona data di inizio e fine.');
      return;
    }
    if (dayjs(form.endDate).isBefore(dayjs(form.startDate), 'day')) {
      setError('La data di fine non può essere prima della data di inizio.');
      return;
    }
    if (!form.startTime || !form.endTime) {
      setError('Seleziona ora di inizio e fine.');
      return;
    }

    const payload: BookingInput = {
      gruppoScout: form.gruppoScout.trim(),
      branca: form.branca,
      numeroRagazzi: numero,
      numeroAdulti: adulti,
      referenteNome: form.referenteNome.trim(),
      telefono: normalizePhone(form.telefono),
      email: form.email.trim(),
      startDate: form.startDate,
      endDate: form.endDate,
      startTime: form.startTime,
      endTime: form.endTime,
      altreInfo: form.altreInfo.trim(),
    };

    if (!isValidRange(payload)) {
      setError('L’orario di fine deve essere successivo all’inizio.');
      return;
    }

    setSaving(true);
    try {
      await updateBooking(
        booking.id,
        payload,
        booking.status,
        booking.googleCalendarEventId
      );
      onSaved();
      onClose();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Impossibile salvare le modifiche.'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullScreen={isMobile}
      fullWidth
      maxWidth="sm"
      scroll="paper"
      slotProps={{
        paper: {
          sx: isMobile
            ? { m: 0, borderRadius: 0 }
            : {
                borderRadius: '16px',
                border: `1px solid ${colors.outlineVariant}`,
                maxHeight: '90vh',
              },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontWeight: 700,
          color: colors.primary,
          pr: 1,
        }}
      >
        Modifica prenotazione
        <IconButton aria-label="Chiudi" onClick={onClose} disabled={saving}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2 }}>
        <Stack spacing={2.25}>
          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            label="Gruppo"
            value={form.gruppoScout}
            onChange={update('gruppoScout')}
            required
            fullWidth
          />
          <TextField
            select
            label="Per che branca prenoti"
            value={form.branca}
            onChange={update('branca')}
            required
            fullWidth
          >
            {BRANCHE.map((b) => (
              <MenuItem key={b} value={b}>
                {BRANCA_LABELS[b]}
              </MenuItem>
            ))}
          </TextField>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Ragazzi previsti"
              type="number"
              value={form.numeroRagazzi}
              onChange={update('numeroRagazzi')}
              required
              fullWidth
              slotProps={{ htmlInput: { min: 0, step: 1 } }}
            />
            <TextField
              label="Adulti previsti"
              type="number"
              value={form.numeroAdulti}
              onChange={update('numeroAdulti')}
              required
              fullWidth
              slotProps={{ htmlInput: { min: 1, step: 1 } }}
            />
          </Stack>
          <TextField
            label="Nome e cognome referente"
            value={form.referenteNome}
            onChange={update('referenteNome')}
            required
            fullWidth
          />
          <TextField
            label="Numero di telefono"
            type="tel"
            value={form.telefono}
            onChange={update('telefono')}
            required
            fullWidth
            helperText="Accetta anche prefisso internazionale (+39…)"
          />
          <TextField
            label="Email"
            type="email"
            value={form.email}
            onChange={update('email')}
            required
            fullWidth
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Data inizio"
              type="date"
              value={form.startDate}
              onChange={(e) => {
                const startDate = e.target.value;
                setForm((prev) =>
                  prev
                    ? {
                        ...prev,
                        startDate,
                        endDate:
                          prev.endDate < startDate ? startDate : prev.endDate,
                      }
                    : prev
                );
                setError(null);
              }}
              required
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              label="Data fine"
              type="date"
              value={form.endDate}
              onChange={(e) => {
                setForm((prev) =>
                  prev ? { ...prev, endDate: e.target.value } : prev
                );
                setError(null);
              }}
              required
              fullWidth
              slotProps={{
                inputLabel: { shrink: true },
                htmlInput: { min: form.startDate },
              }}
            />
          </Stack>

          <TimeRangeFields
            idPrefix="edit-booking"
            startTime={form.startTime}
            endTime={form.endTime}
            sameDay={sameDay}
            onStartChange={(startTime) => {
              setForm((prev) => {
                if (!prev) return prev;
                const next = { ...prev, startTime };
                if (
                  prev.startDate === prev.endDate &&
                  prev.endTime &&
                  prev.endTime <= startTime
                ) {
                  next.endTime = '';
                }
                return next;
              });
              setError(null);
            }}
            onEndChange={(endTime) => {
              setForm((prev) => (prev ? { ...prev, endTime } : prev));
              setError(null);
            }}
          />

          <TextField
            label="Altre informazioni"
            value={form.altreInfo}
            onChange={update('altreInfo')}
            fullWidth
            multiline
            minRows={3}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={saving} variant="outlined">
          Annulla
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          variant="contained"
          startIcon={
            saving ? <CircularProgress size={18} color="inherit" /> : undefined
          }
        >
          Salva
        </Button>
      </DialogActions>
    </Dialog>
  );
}
